import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Tooltip,
  Badge,
  Tab,
  Tabs,
} from '@mui/material';
import CyberSpinner from './CyberSpinner';
import {
  People as FriendsIcon,
  PersonAdd as AddFriendIcon,
  PersonRemove as RemoveIcon,
  MyLocation as LocationIcon,
  LocationOff as LocationOffIcon,
  Share as ShareIcon,
  Check as AcceptIcon,
  Close as RejectIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { friendsService, Friend, FriendRequest, FriendLocation } from '../services/friendsService';
import { telegram } from '../services/telegram';
import { useAutoDismiss } from '../hooks/useAutoDismiss';
import { useTranslation } from '../i18n/translations';

interface FriendsPanelProps {
  open: boolean;
  onClose: () => void;
  onNavigateToFriend?: (lat: number, lng: number, name: string) => void;
  currentLocation?: { lat: number; lng: number } | null;
}

export const FriendsPanel: React.FC<FriendsPanelProps> = ({
  open,
  onClose,
  onNavigateToFriend,
  currentLocation,
}) => {
  const { t } = useTranslation();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingRequests, setPendingRequests] = useState<FriendRequest[]>([]);
  const [friendLocations, setFriendLocations] = useState<FriendLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [addFriendDialogOpen, setAddFriendDialogOpen] = useState(false);
  const [newFriendUsername, setNewFriendUsername] = useState('');
  const [activeTab, setActiveTab] = useState<'friends' | 'requests'>('friends');
  
  // Global location sharing toggle
  const [globalLocationSharing, setGlobalLocationSharing] = useState(() => {
    return localStorage.getItem('friends-location-sharing') === 'true';
  });

  useAutoDismiss(error, () => setError(null));
  useAutoDismiss(success, () => setSuccess(null), 3000);

  const loadFriends = useCallback(async () => {
    try {
      setLoading(true);
      const result = await friendsService.getMyFriends();
      // Safely extract with defaults
      setFriends(result?.friends || []);
      setPendingRequests(result?.pendingRequests || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load friends');
      // Initialize with empty arrays on error
      setFriends([]);
      setPendingRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFriendLocations = useCallback(async () => {
    try {
      const locations = await friendsService.getFriendsLocations();
      setFriendLocations(locations);
    } catch (err) {
      console.warn('Failed to load friend locations:', err);
    }
  }, []);

  // Load friends when panel opens
  useEffect(() => {
    if (open) {
      loadFriends();
      loadFriendLocations();
    }
  }, [open, loadFriends, loadFriendLocations]);

  // Poll for friend locations when sharing is enabled
  useEffect(() => {
    if (!open || !globalLocationSharing) return;
    
    const interval = setInterval(() => {
      loadFriendLocations();
      
      // Update own location if sharing
      if (currentLocation) {
        friendsService.updateMyLocation(currentLocation.lat, currentLocation.lng);
      }
    }, 10000); // Every 10 seconds
    
    return () => clearInterval(interval);
  }, [open, globalLocationSharing, currentLocation, loadFriendLocations]);

  const handleSendFriendRequest = async () => {
    if (!newFriendUsername.trim()) return;
    
    try {
      await friendsService.sendFriendRequest(newFriendUsername.trim());
      setSuccess(`Friend request sent to @${newFriendUsername}`);
      setNewFriendUsername('');
      setAddFriendDialogOpen(false);
      telegram.hapticFeedback('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send request');
      telegram.hapticFeedback('error');
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      await friendsService.acceptFriendRequest(requestId);
      setSuccess('Friend request accepted!');
      loadFriends();
      telegram.hapticFeedback('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept request');
      telegram.hapticFeedback('error');
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      await friendsService.rejectFriendRequest(requestId);
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      telegram.hapticFeedback('medium');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject request');
    }
  };

  const handleRemoveFriend = async (friendId: string) => {
    try {
      await friendsService.removeFriend(friendId);
      setFriends(prev => prev.filter(f => f.id !== friendId));
      setSuccess('Friend removed');
      telegram.hapticFeedback('medium');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove friend');
    }
  };

  const handleToggleLocationSharing = async (friendId: string, enabled: boolean) => {
    try {
      const updated = await friendsService.toggleLocationSharing(friendId, enabled);
      setFriends(prev => prev.map(f => f.id === friendId ? updated : f));
      telegram.hapticFeedback('light');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sharing');
    }
  };

  const handleGlobalLocationToggle = (enabled: boolean) => {
    setGlobalLocationSharing(enabled);
    localStorage.setItem('friends-location-sharing', enabled.toString());
    telegram.hapticFeedback('light');
  };

  const handleShareInvite = async () => {
    try {
      const link = await friendsService.getInviteLink();
      telegram.shareApp(`👥 Add me as a friend on ZiaWellNav!\n${link}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get invite link');
    }
  };

  const handleNavigateToFriend = (location: FriendLocation) => {
    if (onNavigateToFriend && location.latitude && location.longitude) {
      onNavigateToFriend(
        location.latitude, 
        location.longitude, 
        location.display_name || location.username || 'Friend'
      );
      onClose();
    }
  };

  const getFriendLocation = (friendUserId: string): FriendLocation | undefined => {
    return friendLocations.find(loc => loc.telegram_user_id === friendUserId);
  };

  const pendingCount = pendingRequests?.length || 0;
  const acceptedFriends = friends?.filter(f => f.status === 'accepted') || [];

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.95) 100%)',
          borderRadius: 3,
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <FriendsIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {t('friendsTitle')}
            </Typography>
            {acceptedFriends.length > 0 && (
              <Chip label={acceptedFriends.length} size="small" color="primary" />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={t('friendsShareInvite')}>
              <IconButton size="small" onClick={handleShareInvite}>
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('refresh')}>
              <IconButton size="small" onClick={loadFriends}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2 }}>
        {/* Global Location Sharing Toggle */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 2, 
            bgcolor: 'rgba(96, 165, 250, 0.1)',
            border: '1px solid rgba(96, 165, 250, 0.2)',
            borderRadius: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={globalLocationSharing}
                onChange={(e) => handleGlobalLocationToggle(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {t('friendsShareLocation')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('friendsShareLocationDesc')}
                </Typography>
              </Box>
            }
          />
        </Paper>

        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 2 }}
        >
          <Tab 
            label={t('friendsTabFriends')} 
            value="friends" 
            icon={<FriendsIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label={
              <Badge badgeContent={pendingCount} color="error">
                {t('friendsTabRequests')}
              </Badge>
            } 
            value="requests"
          />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CyberSpinner size={40} />
          </Box>
        ) : activeTab === 'friends' ? (
          <>
            {/* Add Friend Button */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddFriendIcon />}
              onClick={() => setAddFriendDialogOpen(true)}
              sx={{ mb: 2 }}
            >
              {t('friendsAddByUsername')}
            </Button>

            {/* Friends List */}
            {acceptedFriends.length === 0 ? (
              <Box textAlign="center" py={4}>
                <FriendsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  {t('friendsNoFriends')}
                </Typography>
              </Box>
            ) : (
              <List>
                {acceptedFriends.map((friend) => {
                  const location = getFriendLocation(friend.telegram_user_id);
                  const hasLocation = location && location.latitude && location.longitude;
                  
                  return (
                    <ListItem
                      key={friend.id}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.02)',
                        borderRadius: 2,
                        mb: 1,
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                          sx={{ marginTop: '60px' }}
                          badgeContent={
                            hasLocation ? (
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor: '#10b981',
                                  border: '2px solid #0f172a',
                                }}
                              />
                            ) : null
                          }
                        >
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {(friend.display_name || friend.telegram_username || '?')[0].toUpperCase()}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={friend.display_name || `@${friend.telegram_username}`}
                        secondary={
                          hasLocation 
                            ? `📍 Location available` 
                            : friend.friend_shares_location 
                              ? 'Waiting for location...'
                              : 'Location not shared'
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {/* Location sharing toggle */}
                          <Tooltip title={friend.location_sharing ? 'Stop sharing location' : 'Share your location'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleLocationSharing(friend.id, !friend.location_sharing)}
                              color={friend.location_sharing ? 'primary' : 'default'}
                            >
                              {friend.location_sharing ? <LocationIcon /> : <LocationOffIcon />}
                            </IconButton>
                          </Tooltip>
                          
                          {/* Navigate to friend */}
                          {hasLocation && (
                            <Tooltip title="Navigate to friend">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleNavigateToFriend(location)}
                              >
                                <LocationIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {/* Remove friend */}
                          <Tooltip title="Remove friend">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveFriend(friend.id)}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        ) : (
          /* Pending Requests Tab */
          <>
            {(!pendingRequests || pendingRequests.length === 0) ? (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  No pending friend requests
                </Typography>
              </Box>
            ) : (
              <List>
                {pendingRequests.map((request) => (
                  <ListItem
                    key={request.id}
                    sx={{
                      bgcolor: 'rgba(251, 191, 36, 0.1)',
                      borderRadius: 2,
                      mb: 1,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'warning.main' }}>
                        {(request.from_username || '?')[0].toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`@${request.from_username}`}
                      secondary="Wants to be your friend"
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Accept">
                        <IconButton
                          color="success"
                          onClick={() => handleAcceptRequest(request.id)}
                        >
                          <AcceptIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          color="error"
                          onClick={() => handleRejectRequest(request.id)}
                        >
                          <RejectIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Add Friend Dialog */}
      <Dialog
        open={addFriendDialogOpen}
        onClose={() => setAddFriendDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Friend</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Telegram Username"
            placeholder="@username"
            value={newFriendUsername}
            onChange={(e) => setNewFriendUsername(e.target.value.replace('@', ''))}
            sx={{ mt: 1 }}
            helperText="Enter your friend's Telegram username"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFriendDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSendFriendRequest}
            disabled={!newFriendUsername.trim()}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '60px' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '60px' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default FriendsPanel;
